// FinKloz — Cash Template Builder
// Builds the cash template data structure from reconciliation results.
// Mirrors the Excel template format: Summary + per-account detail.

import { BANK_ACCOUNTS } from './bankStatementParser';

// ─── Build summary row for one account ───
function buildSummaryRow(accountNo, accountData, accountConfig) {
  if (!accountData || accountData.status === 'not_started' || (!accountData.statement && !accountData.reconciliation)) {
    return {
      accountNo,
      name: accountConfig?.name || accountNo,
      currency: accountConfig?.currency || 'CAD',
      glEnding: null,
      stmtEnding: null,
      difference: null,
      status: 'not_started',
      stmtDate: null,
      stmtRef: null,
    };
  }

  const rec = accountData.reconciliation;
  const stmt = accountData.statement;
  const isUSD = (accountConfig?.currency || 'CAD') === 'USD';

  // Try multiple sources for statement ending balance
  const stmtEnding = stmt?.metadata?.closingBalance
    || rec?.bankBalance
    || (stmt?.transactions?.length > 0 ? stmt.transactions[stmt.transactions.length - 1].balance : null)
    || null;

  return {
    accountNo,
    name: accountConfig?.name || accountNo,
    currency: accountConfig?.currency || 'CAD',
    glEnding: rec?.glBalance ?? null,
    stmtEnding,
    difference: isUSD ? 'See Tab'
      : (rec?.glBalance != null && stmtEnding != null) ? Math.round((rec.glBalance - stmtEnding) * 100) / 100
      : null,
    status: (() => {
      // ONLY "completed" status = Reconciled (green). Must be explicitly marked.
      if (accountData.status === 'completed' || accountData.status === 'reviewed') return 'reconciled';
      if (!accountData.status || accountData.status === 'not_started') return 'not_started';
      if (accountData.status === 'matched') {
        // Matched but not yet marked complete
        if (isUSD) return 'in_progress';
        const simpleDiff = rec?.glBalance != null && stmtEnding != null ? Math.abs(rec.glBalance - stmtEnding) : null;
        if (simpleDiff !== null && simpleDiff < 5) return 'minor_diff';
        return 'in_progress';
      }
      return 'in_progress';
    })(),
    stmtDate: stmt?.metadata?.periodEnd ?? null,
    stmtRef: stmt?.file?.name ?? null,
  };
}

// ─── Build per-account template data ───
function buildAccountTemplate(accountNo, accountData, accountConfig) {
  const isUSD = (accountConfig?.currency || 'CAD') === 'USD';
  const stmt = accountData?.statement;
  const gl = accountData?.glData;
  const rec = accountData?.reconciliation;
  const items = accountData?.reconcilingItems || [];
  const fx = accountData?.fxConversion;

  // Section A: Reconciliation
  const glEntries = gl?.entries || [];
  const totalDebit = glEntries.reduce((s, e) => s + (e.debit || 0), 0);
  const totalCredit = glEntries.reduce((s, e) => s + (e.credit || 0), 0);

  const sectionA = {
    isUSD,
    openingBalance: gl?.openingBalance ?? stmt?.metadata?.openingBalance ?? 0,
    totalDebits: Math.round(totalDebit * 100) / 100,
    totalCredits: Math.round(totalCredit * 100) / 100,
    glEndingCalculated: rec?.glBalance ?? Math.round((totalDebit - totalCredit) * 100) / 100,
    bankStatementBalance: stmt?.metadata?.closingBalance ?? 0,
    difference: rec?.difference ?? 0,
  };

  // Section B: Reconciling Items
  const sectionB = items.map(item => ({
    description: item.description,
    amount: item.amount,
    note: item.note || '',
    type: item.type,
    action: item.action,
  }));

  // Section C: GL Transaction Detail
  const sectionC = glEntries.map(e => ({
    date: e.date,
    description: e.description,
    journal: e.journal || '',
    currency: e.currency || accountConfig?.currency || 'CAD',
    debit: e.debit || 0,
    credit: e.credit || 0,
    txnDebit: e.txnDebit,
    txnCredit: e.txnCredit,
    runningBalance: e.runningBalance,
  }));

  // Section D: FX Conversion (USD only)
  const sectionD = isUSD && fx ? {
    usdEnding: fx.usdEnding,
    fxRate: fx.fxRate,
    fxRateDate: fx.fxRateDate,
    convertedCAD: fx.convertedCAD,
    glCADEnding: fx.glCADEnding,
    fxJEAmount: fx.fxJEAmount,
  } : null;

  return {
    accountNo,
    name: accountConfig?.name || accountNo,
    currency: accountConfig?.currency || 'CAD',
    sectionA,
    sectionB,
    sectionC,
    sectionD,
  };
}

// ─── Main: build complete template from all accounts ───
export function buildCashTemplate(reconciliationState, accounts = BANK_ACCOUNTS) {
  const accountMap = {};
  for (const a of accounts) accountMap[a.accountNo] = a;

  const summary = [];
  const accountTemplates = [];

  for (const acct of accounts) {
    const data = reconciliationState?.accounts?.[acct.accountNo];
    summary.push(buildSummaryRow(acct.accountNo, data, acct));

    if (data && data.status !== 'not_started') {
      accountTemplates.push(buildAccountTemplate(acct.accountNo, data, acct));
    }
  }

  return {
    companyName: 'Demo Medical Devices Inc.',
    month: reconciliationState?.month || '',
    summary,
    accounts: accountTemplates,
  };
}
